// =============================================================
// Pop-up okna
//
// Dialogy vykreslí server skryté (www/templates/components/popups.php,
// jen aktivní pop-upy v okně od–do pro jazyk stránky). Tady se řeší,
// KDY se otevřou (data-trigger, data-delay), jestli už je návštěvník
// neviděl (data-once) a přístupnost dialogu.
//
// Četnost se pamatuje v úložišti prohlížeče (sessionStorage pro „každé
// sezení", jinak localStorage s časem posledního zobrazení). Je to
// funkční záznam, ne sledování — nic neodchází ze zařízení. Když
// úložiště nejde (anonymní okno, blokované), pop-up se ukáže jednou za
// načtení stránky.
//
// Pop-upy čekají, až návštěvník rozhodne o cookies — lišta souhlasu
// a dialog by se jinak překrývaly a soupeřily o fokus.
// =============================================================

const DAY = 24 * 60 * 60 * 1000;
const PERIOD = { day: DAY, week: 7 * DAY, month: 30 * DAY };
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

let current = null;   // právě otevřený pop-up
const queue = [];     // spuštěné, ale čekající, až se zavře ten otevřený

function storageKey(el) { return 'ln_popup_' + el.dataset.popup; }

function wasSeen(el) {
    const once = el.dataset.once || 'session';
    try {
        if (once === 'session') return sessionStorage.getItem(storageKey(el)) !== null;
        const at = parseInt(localStorage.getItem(storageKey(el)) || '', 10);
        return Number.isFinite(at) && Date.now() - at < (PERIOD[once] || DAY);
    } catch {
        return false;
    }
}

function markSeen(el) {
    const once = el.dataset.once || 'session';
    try {
        if (once === 'session') sessionStorage.setItem(storageKey(el), '1');
        else localStorage.setItem(storageKey(el), String(Date.now()));
    } catch { /* bez úložiště jen jednou za načtení stránky */ }
}

function expired(el) {
    const until = el.dataset.until ? Date.parse(el.dataset.until) : NaN;
    return Number.isFinite(until) && Date.now() > until;
}

function focusables(dialog) {
    return [...dialog.querySelectorAll(FOCUSABLE)].filter(n => n.offsetParent !== null || n === document.activeElement);
}

function open(el) {
    if (el.dataset.popupDone) return;
    if (expired(el)) { el.dataset.popupDone = '1'; return; }
    if (current) { if (!queue.includes(el)) queue.push(el); return; }

    el.dataset.popupDone = '1';
    markSeen(el);
    current = el;

    const dialog = el.querySelector('.ln-popup__dialog');
    const modal  = dialog.getAttribute('aria-modal') === 'true';
    el._lnReturnFocus = modal ? document.activeElement : null;

    el.hidden = false;
    // Třída až v dalším snímku, aby se přechod (jen bez prefers-reduced-motion) spustil.
    requestAnimationFrame(() => el.classList.add('is-open'));

    // Modální okno bere fokus (čtečka přečte titulek); nemodální lišta
    // a rohy fokus návštěvníkovi neberou — nečekaná změna kontextu.
    if (modal) dialog.focus({ preventScroll: true });
}

function close(el) {
    if (current !== el) return;
    el.classList.remove('is-open');
    el.hidden = true;
    current = null;

    const back = el._lnReturnFocus;
    if (back && document.contains(back) && typeof back.focus === 'function') back.focus({ preventScroll: true });

    const next = queue.shift();
    if (next) setTimeout(() => open(next), 400);
}

function onKeydown(e) {
    if (!current) return;
    const dialog = current.querySelector('.ln-popup__dialog');

    if (e.key === 'Escape') { e.preventDefault(); close(current); return; }
    if (e.key !== 'Tab' || dialog.getAttribute('aria-modal') !== 'true') return;

    // Past na Tab: fokus zůstává v modálním dialogu.
    const list = focusables(dialog);
    if (!list.length) { e.preventDefault(); dialog.focus(); return; }
    const first = list[0];
    const last  = list[list.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
    } else if (!dialog.contains(document.activeElement)) {
        e.preventDefault(); first.focus();
    }
}

function scrolledPast(ratio) {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    // Krátká stránka, která se nedá rolovat, je „dočtená" hned.
    return max <= 0 || window.scrollY / max >= ratio;
}

function arm(el) {
    const trigger = el.dataset.trigger || 'page_load';

    if (trigger === 'delay') {
        setTimeout(() => open(el), Math.max(0, parseInt(el.dataset.delay || '0', 10)) * 1000);
    } else if (trigger === 'scroll_50' || trigger === 'scroll_75') {
        const ratio = trigger === 'scroll_50' ? 0.5 : 0.75;
        const check = () => {
            if (!scrolledPast(ratio)) return;
            window.removeEventListener('scroll', check);
            open(el);
        };
        window.addEventListener('scroll', check, { passive: true });
        check();
    } else if (trigger === 'exit_intent') {
        // Odchod myší přes horní okraj okna. Dotyková zařízení nic takového
        // nemají — tam se pop-up se spouštěčem „Opuštění stránky" neukáže.
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        const onOut = (e) => {
            if (e.relatedTarget || e.clientY > 0) return;
            document.removeEventListener('mouseout', onOut);
            open(el);
        };
        document.addEventListener('mouseout', onOut);
    } else {
        open(el);
    }
}

function start(items) {
    items.forEach(arm);
}

function consentPending() {
    // Lišta cookies (vanilla-cookieconsent) zapisuje volbu do cookie cc_cookie.
    return !!document.querySelector('meta[name="ln-consent"]') && !/(?:^|;\s*)cc_cookie=/.test(document.cookie);
}

export function initPopups() {
    const items = [...document.querySelectorAll('.ln-popup[data-popup]')].filter(el => !wasSeen(el) && !expired(el));
    if (!items.length) return;

    items.forEach((el) => {
        el.querySelectorAll('[data-popup-close]').forEach(b => b.addEventListener('click', () => close(el)));
        // Klik na výzvu k akci pop-up zavře — jinak by po návratu (zpět) visel dál.
        el.querySelectorAll('[data-popup-cta]').forEach(a => a.addEventListener('click', () => close(el)));
    });
    document.addEventListener('keydown', onKeydown);

    if (consentPending()) {
        window.addEventListener('cc:onFirstConsent', () => start(items), { once: true });
    } else {
        start(items);
    }
}
